import { CurrentAttempt_QuestionAttemptFragment } from "~/__generated__/graphql"
import {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react"
import { parseISO } from "date-fns"
import { cn } from "~/common/shadcn-utils"
import { secondsToMMSS } from "~/common/secondsToMMSS"

export const useSecondsRemaining = ({
  currentAttempt,
}: {
  currentAttempt?: CurrentAttempt_QuestionAttemptFragment | null
}) => {
  if (!currentAttempt) return null
  const timeLimitSeconds =
    currentAttempt.question.questionGroup.timeLimitSeconds

  if (typeof timeLimitSeconds !== "number" || isNaN(timeLimitSeconds))
    return null

  const startedAt = parseISO(currentAttempt.createdAt)
  const secondsSpent = new Date().getTime() / 1000 - startedAt.getTime() / 1000
  const secondsRemaining =
    timeLimitSeconds - secondsSpent - currentAttempt.previousAttemptTimeSeconds
  return secondsRemaining > 0 ? secondsRemaining : 0
}

export interface TimerHandles {
  stop: () => void
}

export const Timer = forwardRef<TimerHandles, { secondsRemaining: number }>(
  ({ secondsRemaining: initialSecondsRemaining }, ref) => {
    const [remaining, setRemaining] = useState(initialSecondsRemaining)
    const [running, setRunning] = useState(true)
    const startTime = useRef(Date.now())
    const intervalRef = useRef<NodeJS.Timeout | null>(null)

    useImperativeHandle(ref, () => ({
      stop: () => {
        setRunning(false)
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
        console.log("Timer stopped at:", remaining)
      },
    }))

    useEffect(() => {
      if (!running) return

      startTime.current = Date.now()
      intervalRef.current = setInterval(() => {
        const elapsed = (Date.now() - startTime.current) / 1000
        const newRemaining = Math.max(initialSecondsRemaining - elapsed, 0)
        setRemaining(newRemaining)
      }, 100)
      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current)
      }
    }, [initialSecondsRemaining, running])

    return (
      <div className={cn({ "text-communist": remaining <= 30 })}>
        Question Time Remaining - {secondsToMMSS(remaining)}
      </div>
    )
  }
)
